import { z } from 'zod';
/**
 * Bản tóm tắt một task — APM-AI-g (`plan-ai-features.md` mục 1).
 *
 * ## Vì sao mọi mục đều phải trỏ về `sources`
 *
 * Người mở bản tóm tắt là người **chưa đọc** 40 bình luận. Họ không có cách nào
 * biết câu "đội đang chờ pháp chế duyệt" đến từ một bình luận thật hay từ trí
 * tưởng tượng của model. Số hiệu dẫn chứng bấm được là thứ biến bản tóm tắt từ
 * "một câu chuyện nghe hợp lý" thành "một lối tắt kiểm được": bấm vào là nhảy
 * tới đúng bình luận hoặc đúng event đã sinh ra câu ấy.
 *
 * Grounding check của harness (APM-H-n) kiểm phần Id: `sources[].id` phải có
 * mặt trong kết quả tool của chính lượt chạy đó, nên model không bịa được số
 * hiệu để trông có căn cứ.
 */
export const TaskSummarySource = z.object({
    kind: z.enum(['comment', 'event']),
    /** Id của bình luận hoặc của dòng `event_log` — phải xuất hiện trong kết quả tool. */
    id: z.string().min(1).max(64),
});
export const TaskSummary = z.object({
    /** Một câu: task đang ở đâu. Đây là dòng người đọc lướt qua và dừng lại. */
    statusLine: z.string().min(1).max(400),
    /**
     * Ai đang chờ ai. Rỗng là một câu trả lời hợp lệ — và là câu trả lời **tốt**:
     * "không ai chờ ai" khác hẳn "model không tìm thấy gì".
     */
    waitingOn: z
        .array(z.object({
        who: z.string().min(1).max(200),
        what: z.string().min(1).max(400),
        /** Chờ từ bao giờ, ISO 8601 hoặc mô tả ngắn ("từ bình luận ngày 3/4"). */
        since: z.string().max(100).nullable(),
    }))
        .max(10),
    /** Quyết định đã chốt trong luồng bình luận — thứ dễ chìm nhất khi có 40 bình luận. */
    decisions: z
        .array(z.object({
        text: z.string().min(1).max(500),
        at: z.string().max(100).nullable(),
        by: z.string().max(200).nullable(),
    }))
        .max(10),
    openQuestions: z.array(z.string().min(1).max(400)).max(10),
    /** Việc kế tiếp. `null` khi luồng bình luận không đủ để nói — đừng bịa một việc. */
    nextStep: z.string().max(400).nullable(),
    /** Tối thiểu một dẫn chứng: một bản tóm tắt không trỏ về đâu là một bản tóm tắt không kiểm được. */
    sources: z.array(TaskSummarySource).min(1).max(30),
});
/**
 * Bản tóm tắt kèm chỗ đứng của nó trong thời gian.
 *
 * `lastEventId` là khoá cache: task không có event mới thì bản tóm tắt cũ vẫn
 * đúng, và gọi lại model là tiêu tiền cho một câu trả lời y hệt. `cached` nói
 * cho người dùng biết họ đang đọc bản dựng lúc nào — không có nó thì một bản
 * tóm tắt cũ trông y như bản vừa dựng.
 */
export const TaskSummaryView = z.object({
    taskId: z.uuid(),
    summary: TaskSummary,
    generatedAt: z.iso.datetime(),
    cached: z.boolean(),
    lastEventId: z.string(),
    costUsd: z.string(),
});
/**
 * Thân của `POST /tasks/:taskId/summary`.
 *
 * `refresh` là cách người dùng nói "tôi biết có bản cũ, dựng lại đi" — ví dụ
 * sau khi họ tự sửa mô tả task mà không sinh event nào đáng kể. Không có nó thì
 * cách duy nhất để làm mới là chờ ai đó bình luận.
 */
export const TaskSummaryRequest = z.object({
    /** Mã dấu vết do client sinh, để xem AI đang làm gì — APM-1302. */
    runId: z.uuid().optional(),
    refresh: z.boolean().optional(),
});
//# sourceMappingURL=ai-task-summary.js.map